import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, attempts]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === 'completed') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        setTimeout(() => setAttempts(attempts + 1), 2000);
      }
    } catch (error) {
      toast.error('Error checking payment status');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card-base p-12 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">Processing Payment...</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-secondary mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground mb-8">
              Your subscription is now active. Let's start the learning journey!
            </p>
            <Button
              data-testid="go-to-dashboard-btn"
              onClick={() => navigate('/parent/dashboard')}
              className="rounded-full"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {(status === 'failed' || status === 'timeout' || status === 'error') && (
          <>
            <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">✕</span>
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Failed</h1>
            <p className="text-muted-foreground mb-8">
              {status === 'timeout'
                ? 'Payment verification timed out. Please check your email for confirmation.'
                : 'There was an issue processing your payment. Please try again.'}
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate('/parent/dashboard')}
                className="rounded-full"
              >
                Dashboard
              </Button>
              <Button onClick={() => navigate('/subscription')} className="rounded-full">
                Try Again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;